import React, { Component } from "react";
import PropTypes from "prop-types";

import { format } from "d3-format";
import { isDefined, noop, strokeDashTypes } from "../utils";

import {
  getValueFromOverride,
  terminate,
  saveNodeType,
  isHoverForInteractiveType,
} from "./utils";
import EachInteractiveYCoordinate from "./wrapper/EachInteractiveYCoordinate";
import HoverTextNearMouse from "./components/HoverTextNearMouse";
import MouseLocationIndicator from "./components/MouseLocationIndicator";

class InteractiveYCoordinate extends Component {
  constructor(props) {
    super(props);

    this.handleDrag = this.handleDrag.bind(this);
    this.handleDragComplete = this.handleDragComplete.bind(this);
    this.handleDelete = this.handleDelete.bind(this);
    this.handleStart = this.handleStart.bind(this);
    this.handleEnd = this.handleEnd.bind(this);
    this.handleDrawYCoordinate = this.handleDrawYCoordinate.bind(this);
    this.terminate = terminate.bind(this);

    this.saveNodeType = saveNodeType.bind(this);
    this.getSelectionState =
      isHoverForInteractiveType("yCoordinateList").bind(this);

    this.nodes = [];
    this.state = {};
  }
  handleDrag(index, yValue) {
    this.setState({
      override: {
        index,
        yValue,
      },
    });
  }
  handleDragComplete(moreProps) {
    const { override } = this.state;
    if (isDefined(override)) {
      const { yCoordinateList } = this.props;
      const newAlertList = yCoordinateList.map((each, idx) => {
        const selected = idx === override.index;
        return selected
          ? {
              ...each,
              yValue: override.yValue,
              selected,
            }
          : {
              ...each,
              selected,
            };
      });
      const draggedAlert = newAlertList[override.index];
      this.setState(
        {
          override: null,
        },
        () => {
          this.props.onDragComplete(newAlertList, moreProps, draggedAlert);
        },
      );
    }
  }
  handleDrawLine(xyValue) {
    const { current } = this.state;

    if (isDefined(current) && isDefined(current.start)) {
      this.setState({
        current: {
          start: current.start,
          end: xyValue,
        },
      });
    }
  }
  handleDelete(index, moreProps) {
    console.log("X button pressed on YCoordinate");
    const { onDelete, yCoordinateList } = this.props;
    onDelete(yCoordinateList[index], moreProps);
  }
  handleStart() {
    // Start drawing mode - no specific action needed for YCoordinate
  }
  handleEnd(xyValue, moreProps, e) {
    const { enabled } = this.props;
    if (enabled) {
      const { onChoosePosition } = this.props;

      // Extract y-value from click position
      const yValue = xyValue[1];

      // Get appearance from registry - need to extract strings from objects
      const { appearance = {} } = this.props;

      // Helper to extract color from registry format
      const extractColor = (prop, fallback) => {
        if (typeof prop === "string") return prop;
        if (prop && typeof prop === "object" && prop.color) return prop.color;
        return fallback;
      };

      // Helper to extract numeric value from registry format
      const extractNumeric = (prop, fallback) => {
        if (typeof prop === "number") return prop;
        if (prop && typeof prop === "object" && typeof prop.value === "number")
          return prop.value;
        return fallback;
      };

      // Create a complete YCoordinate object with all required properties
      const newYCoordinate = {
        // Generate unique ID
        id: `ycoord_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,

        // Core properties
        yValue: yValue,
        text: "Alert",

        // Style properties (extract strings from registry objects)
        bgFill: extractColor(appearance.bgFill, "#FFFFFF"),
        bgOpacity: extractNumeric(appearance.bgFill?.opacity, 1),
        stroke: extractColor(appearance.stroke, "#607D8B"),
        strokeOpacity: extractNumeric(appearance.stroke?.opacity, 1),
        strokeDasharray: appearance.stroke?.dash || "ShortDash2",
        strokeWidth: extractNumeric(appearance.stroke?.width, 2),
        textFill: extractColor(appearance.font, "#607D8B"),
        fontFamily:
          appearance.font?.fontFamily ||
          "Helvetica Neue, Helvetica, Arial, sans-serif",
        fontSize: extractNumeric(appearance.font?.fontSize, 12),
        fontStyle: "normal",
        fontWeight: "normal",

        // Required complex objects
        textBox: {
          height: 24,
          left: 20,
          padding: { left: 10, right: 5 },
          closeIcon: {
            padding: { left: 5, right: 8 },
            width: 8,
          },
        },
        edge: {
          stroke: extractColor(appearance.stroke, "#607D8B"),
          strokeOpacity: 1,
          strokeWidth: 1,
          fill: extractColor(appearance.bgFill, "#FFFFFF"),
          fillOpacity: 1,
          orient: "right",
          at: "right",
          arrowWidth: 10,
          dx: 0,
          rectWidth: 50,
          rectHeight: 20,
          displayFormat: format(".2f"), // Import format from d3-format
        },

        // Selection state
        selected: true,
      };

      // Create array with all existing YCoordinates plus the new one
      const { yCoordinateList = [] } = this.props;
      const newYCoordinateList = [
        ...yCoordinateList.map((d) => ({ ...d, selected: false })),
        newYCoordinate,
      ];

      onChoosePosition(newYCoordinateList, moreProps, e);
    }
  }
  handleDrawYCoordinate() {
    // This is called on mouse move - no specific action needed for YCoordinate
    // Unlike TrendLine which shows temporary lines, YCoordinate adds immediately on click
  }
  render() {
    const { yCoordinateList, enabled } = this.props;
    const { override } = this.state;
    return (
      <g>
        {yCoordinateList.map((each, idx) => {
          const props = each;
          const key = each.id || `ycoord_${idx}`;
          return (
            <EachInteractiveYCoordinate
              key={key}
              ref={this.saveNodeType(idx)}
              index={idx}
              {...props}
              selected={each.selected}
              yValue={getValueFromOverride(
                override,
                idx,
                "yValue",
                each.yValue,
              )}
              draggable={true}
              onDelete={this.handleDelete}
              onDrag={this.handleDrag}
              onDragComplete={this.handleDragComplete}
              edgeInteractiveCursor="react-stockcharts-move-cursor"
            />
          );
        })}
        {enabled && (
          <MouseLocationIndicator
            enabled={enabled}
            snap={false}
            shouldDisableSnap={() => false}
            onMouseDown={this.handleStart}
            onClick={this.handleEnd}
            onMouseMove={this.handleDrawYCoordinate}
            r={5}
            stroke="#607D8B"
            strokeWidth={2}
            opacity={1}
            disablePan={false}
            data-evo-id="interactive-interactive-ycoordinate"
          />
        )}
      </g>
    );
  }
}

InteractiveYCoordinate.propTypes = {
  onChoosePosition: PropTypes.func.isRequired,
  onDragComplete: PropTypes.func.isRequired,
  onSelect: PropTypes.func,
  onDelete: PropTypes.func,
  appearance: PropTypes.object,

  defaultPriceCoordinate: PropTypes.shape({
    bgFill: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({ color: PropTypes.string, opacity: PropTypes.number }),
    ]).isRequired,
    bgOpacity: PropTypes.number.isRequired,

    stroke: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({ color: PropTypes.string, opacity: PropTypes.number }),
    ]).isRequired,
    strokeDasharray: PropTypes.oneOf(strokeDashTypes).isRequired,
    strokeOpacity: PropTypes.number.isRequired,
    strokeWidth: PropTypes.number.isRequired,

    textFill: PropTypes.string.isRequired,
    fontFamily: PropTypes.string.isRequired,
    fontWeight: PropTypes.string.isRequired,
    fontStyle: PropTypes.string.isRequired,
    fontSize: PropTypes.number.isRequired,
    text: PropTypes.string.isRequired,

    textBox: PropTypes.shape({
      height: PropTypes.number.isRequired,
      left: PropTypes.number.isRequired,
      padding: PropTypes.shape({
        left: PropTypes.number.isRequired,
        right: PropTypes.number.isRequired,
      }),
      closeIcon: PropTypes.shape({
        padding: PropTypes.shape({
          left: PropTypes.number.isRequired,
          right: PropTypes.number.isRequired,
        }),
        width: PropTypes.number.isRequired,
      }),
    }).isRequired,
    edge: PropTypes.shape({
      stroke: PropTypes.string.isRequired,
      strokeOpacity: PropTypes.number.isRequired,
      strokeWidth: PropTypes.number.isRequired,

      fill: PropTypes.string.isRequired,
      fillOpacity: PropTypes.number.isRequired,
    }),
  }).isRequired,

  hoverText: PropTypes.object.isRequired,
  yCoordinateList: PropTypes.array.isRequired,
  enabled: PropTypes.bool.isRequired,
};

InteractiveYCoordinate.defaultProps = {
  onChoosePosition: noop,
  onDragComplete: noop,
  onSelect: noop,
  onDelete: noop,

  defaultPriceCoordinate: {
    bgFill: "#FFFFFF",
    bgOpacity: 1,

    stroke: "#6574CD",
    strokeOpacity: 1,
    strokeDasharray: "ShortDash2",
    strokeWidth: 1,

    textFill: "#6574CD",
    fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif",
    fontSize: 12,
    fontStyle: "normal",
    fontWeight: "normal",
    text: "Alert",
    textBox: {
      height: 24,
      left: 20,
      padding: { left: 10, right: 5 },
      closeIcon: {
        padding: { left: 5, right: 8 },
        width: 8,
      },
    },
    edge: {
      stroke: "#6574CD",
      strokeOpacity: 1,
      strokeWidth: 1,

      fill: "#FFFFFF",
      fillOpacity: 1,
      orient: "right",
      at: "right",
      arrowWidth: 10,
      dx: 0,
      rectWidth: 50,
      rectHeight: 20,
      displayFormat: format(".2f"),
    },
  },
  hoverText: {
    ...HoverTextNearMouse.defaultProps,
    enable: false,
    bgHeight: 18,
    bgWidth: 175,
    text: "Click and drag the edge circles",
  },
  yCoordinateList: [],
};

InteractiveYCoordinate.contextTypes = {
  subscribe: PropTypes.func.isRequired,
  unsubscribe: PropTypes.func.isRequired,
  generateSubscriptionId: PropTypes.func.isRequired,
  chartId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
};

export default InteractiveYCoordinate;
